import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { InAppNotification } from '@interfaces';
import { initialState, InAppNotificationsState } from './notifications.state';
import * as notificationsActions from './notifications.actions';

const normalizeNotificationRelations = (notification: InAppNotification): InAppNotification => ({
  ...notification,
  relatedForeignerId: notification.relatedForeignerId ?? null,
  relatedForeigner: notification.relatedForeigner ?? null,
  relatedUserProcessId: notification.relatedUserProcessId ?? null,
  relatedUserProcess: notification.relatedUserProcess ?? null,
  relatedUserDocumentId: notification.relatedUserDocumentId ?? null,
  relatedUserDocument: notification.relatedUserDocument ?? null,
  relatedCandidateVerificationId: notification.relatedCandidateVerificationId ?? null,
  relatedCandidateVerification: notification.relatedCandidateVerification ?? null,
});

const reducer: ActionReducer<InAppNotificationsState, Action> = createReducer(
  initialState,
  on(
    notificationsActions.getShortlist,
    notificationsActions.markAllAsRead,
    notificationsActions.markAllOfTypeAsRead,
    (state): InAppNotificationsState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    notificationsActions.getShortlistMore,
    (state): InAppNotificationsState => ({
      ...state,
      errorMessage: null,
      loadingMore: true,
    })
  ),
  on(
    notificationsActions.getShortlistSuccess,
    (state, { shortlist }): InAppNotificationsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      shortlist: shortlist.data.map(normalizeNotificationRelations),
      shortlistPagination: { ...shortlist.pagination },
    })
  ),
  on(
    notificationsActions.getShortlistMoreSuccess,
    (state, { shortlist }): InAppNotificationsState => ({
      ...state,
      loadingMore: false,
      errorMessage: null,
      shortlist: [...state.shortlist, ...shortlist.data.map(normalizeNotificationRelations)],
      shortlistPagination: {
        ...shortlist.pagination,
        count: state.shortlistPagination.count + shortlist.pagination.count,
      },
    })
  ),

  on(
    notificationsActions.getUnreadCountSuccess,
    notificationsActions.markAllOfTypeAsReadSuccess,
    (state, { unreadCount }): InAppNotificationsState => {
      return {
        ...state,
        loading: false,
        errorMessage: null,
        unreadCount,
      };
    }
  ),
  on(
    notificationsActions.newNotificationReceived,
    (state, { notification }): InAppNotificationsState => ({
      ...state,
      shortlist: [normalizeNotificationRelations(notification), ...state.shortlist],
      shortlistPagination: {
        ...state.shortlistPagination,
        count: state.shortlistPagination.count + 1,
        all: state.shortlistPagination.all + 1,
      },
    })
  ),
  on(
    notificationsActions.markAsReadSuccess,
    notificationsActions.markAsUnreadSuccess,
    (state, { updatedNotifications }): InAppNotificationsState => {
      return {
        ...state,
        loading: false,
        errorMessage: null,
        shortlist: state.shortlist.map(oldNotification => {
          const newNotification = updatedNotifications.find(({ id }) => id === oldNotification.id);
          return newNotification ? normalizeNotificationRelations(newNotification) : oldNotification;
        }),
      };
    }
  ),
  on(notificationsActions.markAllAsReadSuccess, (state, { updatedShortlist }): InAppNotificationsState => {
    return {
      ...state,
      loading: false,
      errorMessage: null,
      shortlist: updatedShortlist.data.map(normalizeNotificationRelations),
      shortlistPagination: { ...updatedShortlist.pagination },
    };
  }),

  on(
    notificationsActions.getUnreadCountOfTypeSuccess,
    (
      state,
      { unreadCount, notificationType, relatedForeignerId, relatedCandidateVerificationId }
    ): InAppNotificationsState => ({
      ...state,
      loading: false,
      errorMessage: null,
      unreadCountOfType: {
        notificationType,
        unreadCount,
        relatedForeignerId,
        relatedCandidateVerificationId,
      },
    })
  ),

  on(
    notificationsActions.getShortlistError,
    notificationsActions.markAsReadError,
    notificationsActions.markAsUnreadError,
    notificationsActions.markAllAsReadError,
    notificationsActions.getUnreadCountError,
    notificationsActions.getUnreadCountOfTypeError,
    (state, { errorMessage }): InAppNotificationsState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    notificationsActions.getShortlistMoreError,
    (state, { errorMessage }): InAppNotificationsState => ({
      ...state,
      errorMessage,
      loadingMore: false,
    })
  ),

  on(
    notificationsActions.getMaintenanceInfo,
    notificationsActions.setMaintenanceInfo,
    (state, {}): InAppNotificationsState => ({
      ...state,
      loadingMaintenance: true,
    })
  ),
  on(
    notificationsActions.getMaintenanceInfoError,
    notificationsActions.setMaintenanceError,
    (state, { errorMessage }): InAppNotificationsState => ({
      ...state,
      errorMessage,
      loadingMaintenance: false,
    })
  ),

  on(
    notificationsActions.getMaintenanceInfoSuccess,
    notificationsActions.setMaintenanceSuccess,
    notificationsActions.addReceivedMaintenanceInfo,
    (state, { result }): InAppNotificationsState => ({
      ...state,
      loadingMaintenance: false,
      maintenance: { ...result },
    })
  )
);

export function inAppNotificationsReducer(state: InAppNotificationsState, action: Action): InAppNotificationsState {
  return reducer(state, action);
}
